import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  MenuItem,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment
} from '@mui/material';
import {
  MedicalServices,
  Save,
  Cancel,
  Delete,
  Add,
  ExpandMore,
  History,
  EventRepeat,
  Search,
  Clear
} from '@mui/icons-material';
import { consultationAPI, treatmentAPI } from '../../services/api';

function FollowUpConsultations() {
  const [consultations, setConsultations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);

  const [followUpData, setFollowUpData] = useState({
    diagnosis: '',
    notes: '',
    selectedTreatments: []
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadConsultations();
    loadTreatments();
  }, []);

  const loadConsultations = async () => {
    setLoading(true);
    try {
      const response = await consultationAPI.getMyConsultations();
      const consultationsData = response.data.data || response.data;
      setConsultations(consultationsData);
    } catch (err) {
      console.error('Failed to load consultations:', err);
      setError('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const loadTreatments = async () => {
    try {
      const response = await treatmentAPI.getAll();
      setTreatments(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load treatments:', err);
    }
  };

  const loadFollowUps = async (consultationId) => {
    try {
      const response = await consultationAPI.getFollowUps(consultationId);
      setFollowUps(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load follow-ups:', err);
      setError('Failed to load follow-up consultations');
    }
  };

  const handleSelectConsultation = async (consultation) => {
    setSelectedConsultation(consultation);
    await loadFollowUps(consultation.id);
  };

  const handleOpenFollowUpDialog = () => {
    setFollowUpDialogOpen(true);
    setFollowUpData({
      diagnosis: '',
      notes: '',
      selectedTreatments: []
    });
  };

  const handleCloseFollowUpDialog = () => {
    setFollowUpDialogOpen(false);
    setFollowUpData({
      diagnosis: '',
      notes: '',
      selectedTreatments: []
    });
  };

  const handleAddTreatment = (treatmentId) => {
    const treatment = treatments.find(t => t.id === parseInt(treatmentId));
    if (treatment && !followUpData.selectedTreatments.find(t => t.treatmentId === treatment.id)) {
      setFollowUpData({
        ...followUpData,
        selectedTreatments: [
          ...followUpData.selectedTreatments,
          { treatmentId: treatment.id, treatmentName: treatment.name, quantity: 1, notes: '' }
        ]
      });
    }
  };

  const handleRemoveTreatment = (treatmentId) => {
    setFollowUpData({
      ...followUpData,
      selectedTreatments: followUpData.selectedTreatments.filter(t => t.treatmentId !== treatmentId)
    });
  };

  const handleSubmitFollowUp = async () => {
    if (!followUpData.diagnosis) {
      setError('Please enter diagnosis');
      return;
    }

    if (followUpData.selectedTreatments.length === 0) {
      setError('Please add at least one treatment');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await consultationAPI.create({
        visitId: selectedConsultation.visit_id,
        diagnosis: followUpData.diagnosis,
        notes: followUpData.notes,
        treatments: followUpData.selectedTreatments,
        packages: [],
        suggestIpd: false,
        parentConsultationId: selectedConsultation.id,
        isFollowUp: true
      });

      setSuccess('Follow-up consultation created successfully!');
      handleCloseFollowUpDialog();

      // Reload follow-ups
      await loadFollowUps(selectedConsultation.id);
      await loadConsultations();
    } catch (err) {
      console.error('Failed to create follow-up:', err);
      setError('Failed to create follow-up consultation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <EventRepeat sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            Follow-up Consultations
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Your Consultations</Typography>
              <TextField
                fullWidth
                placeholder="Search patient name, code, diagnosis..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            {consultations.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography color="text.secondary">
                  No consultations found
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                {consultations
                  .filter((c) => {
                    if (!searchQuery.trim()) return true;
                    const q = searchQuery.toLowerCase().trim();
                    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
                    const code = (c.patient_code || '').toLowerCase();
                    const diagnosis = (c.diagnosis || '').toLowerCase();
                    return name.includes(q) || code.includes(q) || diagnosis.includes(q);
                  })
                  .map((consultation) => (
                  <Paper
                    key={consultation.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: 'pointer',
                      border: selectedConsultation?.id === consultation.id ? 2 : 1,
                      borderColor: selectedConsultation?.id === consultation.id ? 'primary.main' : 'divider',
                      '&:hover': { bgcolor: 'grey.50' }
                    }}
                    onClick={() => handleSelectConsultation(consultation)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {consultation.first_name} {consultation.last_name}
                      </Typography>
                      <Chip
                        label={consultation.is_follow_up ? 'Follow-up' : 'Initial'}
                        size="small"
                        color={consultation.is_follow_up ? 'secondary' : 'primary'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Patient Code: {consultation.patient_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date: {new Date(consultation.consultation_date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Diagnosis: {consultation.diagnosis?.substring(0, 50)}
                      {consultation.diagnosis?.length > 50 ? '...' : ''}
                    </Typography>
                    {(consultation.prescribed_treatments_count > 0 || consultation.prescribed_packages_count > 0) && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        {consultation.prescribed_treatments_count > 0 && (
                          <Chip
                            label={`${consultation.prescribed_treatments_count} Treatment${consultation.prescribed_treatments_count > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {consultation.prescribed_packages_count > 0 && (
                          <Chip
                            label={`${consultation.prescribed_packages_count} Package${consultation.prescribed_packages_count > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={7}>
            {selectedConsultation ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Consultation Details</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenFollowUpDialog}
                  >
                    Add Follow-up
                  </Button>
                </Box>

                <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedConsultation.first_name} {selectedConsultation.last_name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Patient Code</Typography>
                      <Typography variant="body1">{selectedConsultation.patient_code}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Date</Typography>
                      <Typography variant="body1">
                        {new Date(selectedConsultation.consultation_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Diagnosis</Typography>
                      <Typography variant="body1">{selectedConsultation.diagnosis}</Typography>
                    </Grid>
                    {selectedConsultation.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography variant="body1">{selectedConsultation.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History /> Follow-up History ({followUps.length})
                </Typography>

                {followUps.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                      No follow-up consultations yet
                    </Typography>
                  </Paper>
                ) : (
                  <Box>
                    {followUps.map((followUp, index) => (
                      <Accordion key={followUp.id} defaultExpanded={index === 0}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Typography fontWeight="medium">
                              Follow-up {index + 1}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(followUp.consultation_date).toLocaleDateString()}
                            </Typography>
                            {followUp.prescribed_treatments_count > 0 && (
                              <Chip
                                label={`${followUp.prescribed_treatments_count} Treatment${followUp.prescribed_treatments_count > 1 ? 's' : ''}`}
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">Diagnosis</Typography>
                              <Typography variant="body1">{followUp.diagnosis}</Typography>
                            </Grid>
                            {followUp.notes && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">Notes</Typography>
                                <Typography variant="body1">{followUp.notes}</Typography>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">Doctor</Typography>
                              <Typography variant="body1">{followUp.doctor_name}</Typography>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', minHeight: 300 }}>
                <MedicalServices sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  Select a consultation to view details and add follow-ups
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Follow-up Dialog */}
        <Dialog open={followUpDialogOpen} onClose={handleCloseFollowUpDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventRepeat color="primary" />
              Create Follow-up Consultation
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Diagnosis *"
                  value={followUpData.diagnosis}
                  onChange={(e) => setFollowUpData({ ...followUpData, diagnosis: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={followUpData.notes}
                  onChange={(e) => setFollowUpData({ ...followUpData, notes: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Prescribe Treatments *
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Select Treatment to Add"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddTreatment(e.target.value);
                    }
                  }}
                  value=""
                >
                  <MenuItem value="">Select Treatment</MenuItem>
                  {treatments.map(treatment => (
                    <MenuItem key={treatment.id} value={treatment.id}>
                      {treatment.name} - ₹{treatment.price} ({treatment.duration_minutes} min)
                    </MenuItem>
                  ))}
                </TextField>

                {followUpData.selectedTreatments.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {followUpData.selectedTreatments.map((treatment) => (
                      <Paper key={treatment.treatmentId} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'success.50' }}>
                        <Typography sx={{ flex: 1 }}>{treatment.treatmentName}</Typography>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveTreatment(treatment.treatmentId)}
                        >
                          <Delete />
                        </IconButton>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFollowUpDialog} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFollowUp}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
            >
              {submitting ? 'Creating...' : 'Create Follow-up'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default FollowUpConsultations;
